<template>
  <div class="image-picker mb-5">
    <div
      class="input-slot image-picker mb-5"
      data-cy="image-picker-input"
      :label="label"
      :hint="hint"
      :persistent-hint="true"
    >
      <v-col class="container image-picker-container">
        <!-- Always render v-img so placeholder can overlay when no image -->
        <v-img
          :src="imageUrl"
          :lazy-src="showLazy ? 'https://picsum.photos/128/?blur=5' : undefined"
          contain
          min-height="200"
          max-height="250"
          class="image-surface"
        >
          <div v-if="!imageId" class="no-image-placeholder">
            <svg
              fill="#ffffff"
              viewBox="0 0 256 256"
              xmlns="http://www.w3.org/2000/svg"
              stroke="#ffffff"
              stroke-width="0.00256"
              transform="matrix(-1, 0, 0, 1, 0, 0)"
            >
              <path
                d="M208.00049,31.99963h-160a16.01833,16.01833,0,0,0-16,16V175.97369l-.001.0307.001,31.99524a16.01833,16.01833,0,0,0,16,16h160a16.01833,16.01833,0,0,0,16-16v-160A16.01834,16.01834,0,0,0,208.00049,31.99963Zm-28.68653,80a16.019,16.019,0,0,0-22.62792,0l-44.68555,44.68653L91.314,135.99963a16.02161,16.02161,0,0,0-22.62792,0L48.00049,156.68457V47.99963h160l.00586,92.6922ZM111.99951,92a12,12,0,1,1-12-12A12,12,0,0,1,111.99951,92Z"
              />
            </svg>
          </div>
        </v-img>

        <v-row class="image-actions mt-5 pb-2 ml-0 mr-0" justify="center">
          <v-btn
            id="imagePickerRandomButton"
            :aria-label="t('aria.label.randomizeImage')"
            :ripple="false"
            text
            dense
            color="primary"
            data-cy="image-picker-btn-random"
            :disabled="!images || images.length === 0"
            @click.stop.prevent="random"
            @touchend.stop.prevent="random"
          >
            {{ t('components.imagePicker.random') }}
          </v-btn>

          <!-- iOS-safe dialog: manual open/close, persistent, focus/scroll handling -->
          <v-dialog
            v-model="dialog"
            persistent
            scrollable
            max-width="800px"
            eager
            :retain-focus="false"
            :close-on-content-click="false"
            :z-index="3000"
            content-class="image-picker-dialog-content"
            data-cy="image-picker-dialog"
            fullscreen
            @update:model-value="onDialogInput"
          >
            <template #activator>
              <v-btn
                id="imagePickerChoiceButton"
                :aria-label="t('aria.label.selectImage')"
                :ripple="false"
                text
                dense
                color="primary"
                data-cy="image-picker-btn-pick"
                :disabled="!images || images.length === 0"
                @click.stop="openDialog"
                @touchend.stop="openDialog"
              >
                {{ t('components.imagePicker.pick') }}
              </v-btn>
            </template>

            <image-picker-view
              v-model="imageId"
              data-cy="image-picker-view-close-btn"
              :images="images"
              :categories="categories"
              @close="requestClose"
            />
          </v-dialog>
        </v-row>

        <v-progress-linear indeterminate :active="loading" />
      </v-col>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, onBeforeUnmount } from 'vue';
import { useLabel } from '@/composables/useLabel';
import ImagePickerView from '@/views/image/ImagePickerIOSView.vue';
import { DonationImage } from '@/interfaces/donation/donationImage';

export default defineComponent({
  components: { ImagePickerView },
  props: {
    modelValue: { type: Number, default: null },
    label: { type: String, default: null },
    hint: { type: String, default: null },
    images: { type: Array as () => DonationImage[], default: () => [] },
    categories: { type: Array, default: () => [] },
    showLazy: { type: Boolean, default: true },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const dialog = ref(false);
    const { t } = useLabel();
    const canClose = ref(false);
    const loading = ref(false);
    const imageId = ref(props.modelValue as number | null);
    const scrollYLock = ref(0);

    const imageUrl = computed((): string | undefined => {
      const list = Array.isArray(props.images) ? (props.images as DonationImage[]) : [];
      const image = list.find((i) => i.id === imageId.value);
      return image ? image.url : undefined;
    });

    watch(
      imageId,
      (val: number | null) => {
        if (val !== null && val !== undefined) emit('update:modelValue', val);
      },
      { immediate: true }
    );

    watch(
      () => props.modelValue,
      (val: number | null) => {
        imageId.value = val;
      }
    );

    watch(dialog, (open: boolean) => {
      if (open) lockBodyScroll();
      else unlockBodyScroll();
    });

    onBeforeUnmount(() => {
      unlockBodyScroll();
    });

    const openDialog = () => {
      canClose.value = false;
      dialog.value = true;
    };

    const requestClose = () => {
      canClose.value = true;
      // double rAF to let Vuetify finish internal transitions before closing
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          dialog.value = false;
          setTimeout(() => {
            canClose.value = false;
          }, 50);
        });
      });
    };

    const onDialogInput = (nextOpen: boolean) => {
      if (nextOpen) {
        dialog.value = true;
        return;
      }
      // Prevent accidental close unless explicitly allowed by child via @close
      dialog.value = !canClose.value;
    };

    const lockBodyScroll = () => {
      scrollYLock.value = window.scrollY || window.pageYOffset || 0;
      const b = document.body.style;
      b.position = 'fixed';
      b.top = `-${scrollYLock.value}px`;
      b.left = '0';
      b.right = '0';
      b.width = '100%';
    };

    const unlockBodyScroll = () => {
      const y = scrollYLock.value || 0;
      const b = document.body.style;
      b.position = '';
      b.top = '';
      b.left = '';
      b.right = '';
      b.width = '';
      window.scrollTo(0, y);
    };

    const random = () => {
      loading.value = true;
      const list = Array.isArray(props.images) ? props.images : [];
      if (!list.length) {
        loading.value = false;
        return;
      }
      let newId = list[Math.floor(Math.random() * list.length)].id;
      if (list.length > 1) {
        let guard = 0;
        while (newId === imageId.value && guard++ < 10) {
          newId = list[Math.floor(Math.random() * list.length)].id;
        }
      }
      imageId.value = newId;
      // Assuming $matomo is available, but since no mixins, perhaps need to import or skip
      // For now, skip as it's not essential
      loading.value = false;
    };

    return {
      t,
      dialog,
      canClose,
      loading,
      imageId,
      imageUrl,
      openDialog,
      requestClose,
      onDialogInput,
      random,
    };
  },
});
</script>

<style>
.image-picker .v-input__slot,
.image-picker .input-slot {
  flex-direction: column;
  align-items: start;
}
.image-picker-dialog-content {
  overflow: hidden !important; /* prevent background scroll bleed on iOS */
}
</style>

<style scoped lang="scss">
#imagePickerRandomButton,
#imagePickerChoiceButton {
  text-transform: none;
  text-decoration: underline;
  font-size: 14px;
}

#imagePickerChoiceButton {
  margin-left: 16px;
}

.v-image.image-surface {
  border-radius: 12px;
}

.no-image-placeholder {
  background: #dce2e5;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  inset: 0;
  z-index: 10;
  svg {
    width: 128px;
    height: 128px;
  }
}

.container {
  width: 100%;
  flex-shrink: 0;
  padding: 0 !important;
  display: flex;
  min-width: 100%;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  > * {
    flex: 1;
    min-width: 50%;
  }

  .image-actions {
    padding-left: 24px;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    margin: 0;
    gap: 8px;
  }

  .v-image {
    width: 50%;
    border-radius: 8px;
  }
}

.image {
  background-color: grey;
  height: 200px;
  width: 100%;
}

@media (max-width: 1080px) {
  .image-picker-container {
    display: flex;
    flex-direction: column !important;

    .v-image {
      min-width: 100%;
    }

    .image-actions {
      padding-left: 0;
    }

    #imagePickerRandomButton,
    #imagePickerChoiceButton {
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: var(--box-rounding);
      height: var(--button-height);
      color: var(--primary-color);
      min-width: 120px;
      text-align: center;
      text-transform: uppercase;
      padding: 2px 8px;
    }

    #imagePickerRandomButton {
      padding-left: 0;
      border: none;
    }
    #imagePickerChoiceButton {
      border: 1px solid var(--primary-color);
    }
  }
}
</style>
